import {
  CustomFieldResourceType,
  customFieldResourceTypeLabels,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import { Input } from "@repo/ui/Inputs/input";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useMemo } from "react";

// Support contact, interview, and customer interaction (grading) resource types
export type UserCustomFieldSupportedResourceType =
  | typeof CustomFieldResourceType.CONTACT
  | typeof CustomFieldResourceType.INTERVIEW
  | typeof CustomFieldResourceType.GRADING;

const contactResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.CONTACT],
    value: CustomFieldResourceType.CONTACT,
  };

const interviewResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.INTERVIEW],
    value: CustomFieldResourceType.INTERVIEW,
  };

const customerInteractionResourceTypeOption: SimpleSelectOption<UserCustomFieldSupportedResourceType> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.GRADING],
    value: CustomFieldResourceType.GRADING,
  };

const fieldTypeOptions: readonly SimpleSelectOption<CustomFieldType>[] = [
  {
    label: "Text",
    value: CustomFieldType.Text,
  },
  {
    label: "Date",
    value: CustomFieldType.Date,
  },
  {
    label: "Date and time",
    value: CustomFieldType.DateAndTime,
  },
  {
    label: "Yes/No",
    value: CustomFieldType.Boolean,
  },
  {
    label: "Number",
    value: CustomFieldType.Number,
  },
] as const;

export interface UserCustomFieldDefinitionFormProps {
  resourceType: UserCustomFieldSupportedResourceType | null;
  displayName: string;
  fieldName: string;
  /** Message to show under the key input when `fieldName` is not a usable key. */
  fieldNameError?: string;
  fieldType: CustomFieldType | null;
  showInterviewResourceType: boolean;
  showCustomerInteractionResourceType: boolean;
  onResourceTypeChange: (value: UserCustomFieldSupportedResourceType) => void;
  onFieldNameChange?: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onFieldTypeChange: (value: CustomFieldType) => void;
}

export const UserCustomFieldDefinitionForm: FC<
  UserCustomFieldDefinitionFormProps
> = ({
  resourceType,
  displayName,
  fieldName,
  fieldNameError,
  fieldType,
  showInterviewResourceType,
  showCustomerInteractionResourceType,
  onResourceTypeChange,
  onFieldNameChange,
  onDisplayNameChange,
  onFieldTypeChange,
}) => {
  const resourceTypeOptions = useMemo(() => {
    const options: SimpleSelectOption<UserCustomFieldSupportedResourceType>[] =
      [contactResourceTypeOption];
    if (showInterviewResourceType) {
      options.push(interviewResourceTypeOption);
    }
    if (showCustomerInteractionResourceType) {
      options.push(customerInteractionResourceTypeOption);
    }
    return options;
  }, [showInterviewResourceType, showCustomerInteractionResourceType]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <SimpleSelect<UserCustomFieldSupportedResourceType>
          label="Resource"
          options={resourceTypeOptions}
          value={resourceType}
          onChange={onResourceTypeChange}
          placeholder="Select resource type"
        />
        <p className="text-sm text-muted-foreground">
          Specify which type of resource this custom field belongs to.
        </p>
      </div>

      <Input
        label="Name"
        id="display_name"
        value={displayName}
        onChange={(e) => onDisplayNameChange(e.target.value)}
        placeholder="CRM opportunity ID"
        description="Name of the custom field. This will be displayed and can be used in message templates."
        maxLength={200}
      />

      <Input
        label="Key"
        value={fieldName}
        isDisabled={onFieldNameChange === undefined}
        onChange={(event) => onFieldNameChange?.(event.target.value)}
        placeholder="crm_opportunity_id"
        description="Key of the custom field. This will be used in the public API. Accepted characters are lowercase letters, numbers, and underscores."
        errorDescription={fieldNameError}
        maxLength={100}
      />

      <SimpleSelect<CustomFieldType>
        label="Type"
        options={fieldTypeOptions}
        value={fieldType}
        onChange={onFieldTypeChange}
        placeholder="Select field type"
      />
    </div>
  );
};
